/**
 * Fetch wrappers for the library server routes.
 *
 * All calls go through `request()` which:
 *   - prepends /api/library
 *   - includes cookies (so Better Auth sessions carry)
 *   - throws ApiError with structured fields on non-2xx
 *
 * Keep this file thin: the shape of each endpoint lives in the individual
 * functions, so a server change is a single-file edit here plus the
 * corresponding caller.
 *
 * The request/query-building core is shared with apps/admin and
 * apps/store via src/shared/api-fetch.js (see that file for what's
 * common vs. what's app-specific). This module still hand-rolls a few
 * endpoints that intentionally bypass the shared `request()` helper —
 * `setFavorite`, `toggleListReaction`, `getListReactions`, `getUserRoms`,
 * `uploadUserRom`, `deleteUserRom`, `getFeatures`, `getSession`,
 * `getTrendingGames` — because they hit main-app routes
 * outside `/api/library`, and (for the last three) swallow errors into a
 * default value rather than throwing. That divergence is preserved
 * exactly as it was pre-refactor. `getFeatures` itself is now a
 * re-export of src/shared/features.js (its fetch-and-cache logic was
 * byte-identical to apps/store's copy), but the bypass-`request()` and
 * swallow-errors behavior described above is unchanged.
 */
import { ApiError, request as sharedRequest } from '../../../src/shared/api-fetch.js';
import { getFeatures as sharedGetFeatures } from '../../../src/shared/features.js';

export { ApiError };

function request(path, opts = {}) {
  return sharedRequest(path, { basePath: '/api/library', ...opts });
}

const resourceCache = new Map();
let cacheSessionUser = undefined;

function cachedResource(key, load) {
  if (!resourceCache.has(key)) {
    const promise = Promise.resolve().then(load).catch((err) => {
      resourceCache.delete(key);
      throw err;
    });
    resourceCache.set(key, promise);
  }
  return resourceCache.get(key);
}

function invalidateResource(prefix) {
  for (const key of resourceCache.keys()) {
    if (key === prefix || key.startsWith(`${prefix}:`)) resourceCache.delete(key);
  }
}

export function clearLibraryResourceCaches() {
  resourceCache.clear();
}

// ── Catalog ──────────────────────────────────────────────────────────

/**
 * Fetch the library catalog. All query params are optional.
 * Returns { items: [...], total, offset, limit }.
 *
 * Pass an AbortController.signal in `opts` to cancel an in-flight
 * request when the caller's intent has changed (e.g. the user typed
 * another character in the search box). Callers must handle the
 * `AbortError` rejection; `reloadCatalog` in index-screen.js shows
 * the idiom.
 */
export function getCatalog(query = {}, opts = {}) {
  return request('/catalog', { query, signal: opts.signal });
}

/** Evaluate an unsaved filter rule server-side and return a bounded preview. */
export function previewCatalog(rule, limit = 12, opts = {}) {
  return request('/catalog/preview', {
    method: 'POST',
    body: { rule, limit },
    query: opts.query,
    signal: opts.signal,
  });
}

// ── Saved custom filters (Phase 6) ───────────────────────────────────

/** List the signed-in user's saved filters. */
export function getFilters() {
  return cachedResource('filters', () => request('/filters'));
}

/** Get one saved filter by id. */
export function getFilter(id) {
  return request(`/filters/${encodeURIComponent(id)}`);
}

/**
 * Field schema (allowlist) for the visual filter builder — returns
 * { fields: { system: { type, ops[], multi }, title: {...}, ... } }.
 */
export function getFilterSchema() {
  return request('/filter-schema');
}

/** Create a saved filter. Body: { name, rule }. */
export function createFilter(name, rule) {
  return request('/filters', { method: 'POST', body: { name, rule } })
    .then((result) => { invalidateResource('filters'); return result; });
}

/** Patch an existing filter. Body: { name?, rule? }. */
export function updateFilter(id, patch) {
  return request(`/filters/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch })
    .then((result) => { invalidateResource('filters'); return result; });
}

/** Delete a saved filter. */
export function deleteFilter(id) {
  return request(`/filters/${encodeURIComponent(id)}`, { method: 'DELETE' })
    .then((result) => { invalidateResource('filters'); return result; });
}

/**
 * Validate a rule tree without saving — returns { ok: true } or the
 * ApiError thrown on 400. Useful for inline feedback in the builder UI.
 */
export function previewFilter(rule) {
  return request('/filters/preview', { method: 'POST', body: { rule } });
}

// ── Tags ─────────────────────────────────────────────────────────────

/**
 * Fetch all tags with usage counts.
 * Returns [{ id, slug, label, kind, count }, ...].
 */
export function getTags(query = {}) {
  return request('/tags', { query });
}

/**
 * Fetch tags for a specific game.
 * Returns [{ id, slug, label, kind, source }, ...].
 */
export function getGameTags(gameId) {
  return request(`/games/${encodeURIComponent(gameId)}/tags`);
}

// ── Favorites ────────────────────────────────────────────────────────

/**
 * Toggle a ROM's favorite state. The `/api/favorites` endpoint is
 * shared with the main app and mutates `user_favorites` (soft-delete
 * on unfavorite, revive on re-favorite). Requires auth.
 *
 * Note: this is NOT under /api/library — it's a main-app endpoint, so
 * we bypass the `request()` helper's `/api/library` prefix.
 */
export async function setFavorite(systemId, romHash, favorited) {
  const res = await fetch('/api/favorites', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ system_id: systemId, rom_hash: romHash, favorited }),
  });
  if (!res.ok) {
    let parsed = null;
    try { parsed = await res.json(); } catch { /* ignore */ }
    throw new ApiError(res.status, parsed);
  }
  return res.json();
}

// ── My ROMs (user uploads/claims) ────────────────────────────────────
// These hit /api/user-roms (main-app endpoints, requireAuth), not
// /api/library — so they bypass the `request()` helper like setFavorite.

async function userRomsFetch(path, opts = {}) {
  const res = await fetch(path, { credentials: 'include', ...opts });
  if (!res.ok) {
    let parsed = null;
    try { parsed = await res.json(); } catch { /* ignore */ }
    throw new ApiError(res.status, parsed);
  }
  return res.json();
}

/** List the signed-in user's uploaded/claimed ROMs → { roms: [...] }. */
export function getUserRoms() {
  return userRomsFetch('/api/user-roms');
}

/**
 * Upload one ROM's bytes (zip already unwrapped by the caller). Returns the
 * server row: { hash, matched, claimed, deduplicated?, ... }. Fetch header
 * values must be ISO-8859-1, so the name is always percent-encoded with the
 * marker header — the server decodes before storing (round-trips unicode).
 */
export function uploadUserRom(system, bytes, filename) {
  return userRomsFetch(`/api/user-roms/${encodeURIComponent(system)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-ROM-Name': encodeURIComponent(filename),
      'X-ROM-Name-Encoded': '1',
    },
    body: bytes,
  });
}

/** Delete an upload/claim row (and its blob when one is stored). */
export function deleteUserRom(system, hash) {
  return userRomsFetch(
    `/api/user-roms/${encodeURIComponent(system)}/${encodeURIComponent(hash)}`,
    { method: 'DELETE' },
  );
}

// ── Game lists (Phase 7) ─────────────────────────────────────────────

/**
 * Dispatch a `library:lists-changed` CustomEvent on document. Any UI
 * that shows list metadata (the sidebar "My lists" section with its
 * item-count pills, the /library/lists overview) listens for this and
 * re-fetches. Keeps the data sources coordinated without each mutation
 * site having to know about every listener. Safe to call from any
 * context — if there's no listener, it's a no-op.
 */
function emitListsChanged() {
  invalidateResource('lists');
  if (typeof document === 'undefined') return;
  document.dispatchEvent(new CustomEvent('library:lists-changed'));
}

/**
 * Fetch the signed-in user's lists (with item counts). Pass
 * { game_id } to include a contains_game boolean on every list.
 * Returns [{ id, name, description, visibility, item_count, ... }, ...].
 */
export function getLists(query = {}) {
  const key = `lists:${new URLSearchParams(Object.entries(query)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([name, value]) => [name, String(value)])).toString()}`;
  return cachedResource(key, () => request('/lists', { query }));
}

export function getPublicLists(query = {}) {
  return request('/lists/public', { query });
}

/**
 * Fetch one list by id, including its items (with joined game metadata).
 * Public lists work unauthenticated; private lists 404 for non-owners.
 */
export function getList(id) {
  return request(`/lists/${encodeURIComponent(id)}`);
}

// Every mutation below fires `library:lists-changed` on success so
// anyone rendering list metadata stays up to date without manual
// plumbing. Wrapping rather than calling emitListsChanged() at each
// callsite keeps the convention enforced in one place.

/** Create a new list. Body: { name, description?, visibility? }. */
export async function createList({ name, description, visibility } = {}) {
  const result = await request('/lists', {
    method: 'POST',
    body: { name, description, visibility },
  });
  emitListsChanged();
  return result;
}

/** Partial update. Any subset of { name, description, visibility }. */
export async function updateList(id, patch) {
  const result = await request(`/lists/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
  emitListsChanged();
  return result;
}

/** Delete a list and all its items. */
export async function deleteList(id) {
  const result = await request(`/lists/${encodeURIComponent(id)}`, { method: 'DELETE' });
  emitListsChanged();
  return result;
}

/**
 * Add a game to a list. Returns `{ item, added }` where `added` is
 * false if the game was already present (idempotent). Emits the
 * change event regardless since UIs may still want to re-check
 * (e.g. a list the user thought was empty may now have entries from
 * another tab).
 */
export async function addListItem(listId, gameId, note) {
  const result = await request(`/lists/${encodeURIComponent(listId)}/items`, {
    method: 'POST',
    body: { game_id: gameId, note },
  });
  emitListsChanged();
  return result;
}

/** Remove a game from a list. */
export async function removeListItem(listId, gameId) {
  const result = await request(
    `/lists/${encodeURIComponent(listId)}/items/${encodeURIComponent(gameId)}`,
    { method: 'DELETE' },
  );
  emitListsChanged();
  return result;
}

/**
 * Reorder items in a list. Body: { order: [gameId, gameId, ...] }.
 * Positions are rewritten in array order (1-based). game_ids not in
 * the order array are left at their current position — so you can
 * reorder a subset without renumbering the rest.
 */
export async function reorderListItems(listId, gameIds) {
  const result = await request(
    `/lists/${encodeURIComponent(listId)}/reorder`,
    { method: 'POST', body: { order: gameIds } },
  );
  emitListsChanged();
  return result;
}

// ── My Queue (singleton system list) ─────────────────────────────────
// The queue is a `kind: 'queue'` list the server lazily creates on
// first access. These wrappers exist so one-click queue buttons never
// need to discover a list id first.

/**
 * Resolve-or-create the signed-in user's queue, including items.
 * Same shape as getList(id) plus `kind: 'queue'`. 401 when signed out.
 */
export function getQueue() {
  return request('/queue');
}

/** One-click add. Returns { item, added, queue_id }. */
export async function addQueueItem(gameId) {
  const result = await request('/queue/items', {
    method: 'POST',
    body: { game_id: gameId },
  });
  emitListsChanged();
  return result;
}

/** One-click remove. */
export async function removeQueueItem(gameId) {
  const result = await request(`/queue/items/${encodeURIComponent(gameId)}`, {
    method: 'DELETE',
  });
  emitListsChanged();
  return result;
}

// ── List reactions ──────────────────────────────────────────────────
// These hit /api/reactions (main-app endpoint), not /api/library.

export async function toggleListReaction(listId, emoji) {
  const res = await fetch('/api/reactions', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ target_type: 'list', target_id: String(listId), emoji }),
  });
  if (!res.ok) {
    let parsed = null;
    try { parsed = await res.json(); } catch { /* ignore */ }
    throw new ApiError(res.status, parsed);
  }
  return res.json();
}

export async function getListReactions(listId) {
  const res = await fetch(
    `/api/reactions?target_type=list&target_ids=${encodeURIComponent(listId)}`,
    { credentials: 'include' },
  );
  if (!res.ok) {
    let parsed = null;
    try { parsed = await res.json(); } catch { /* ignore */ }
    throw new ApiError(res.status, parsed);
  }
  return res.json();
}

// ── Genres ───────────────────────────────────────────────────────────

/**
 * Fetch distinct genres with usage counts.
 * Returns [{ genre: 'Action', count: 120 }, ...].
 */
export function getGenres(query = {}) {
  const key = `genres:${new URLSearchParams(Object.entries(query)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([name, value]) => [name, String(value)])).toString()}`;
  return cachedResource(key, () => request('/genres', { query }));
}

// ── Game detail ──────────────────────────────────────────────────────

/**
 * Fetch the full detail payload for a single game.
 * Returns { game, variants, achievements, user_stats, related }.
 */
export function getGame(system, slug) {
  return request(`/game/${encodeURIComponent(system)}/${encodeURIComponent(slug)}`);
}

// ── Artwork helpers (not fetched via this module — browser resolves <img src>
// against /api/library/artwork/cover/:game_id directly. The URL builder is
// exported so screens don't hardcode the path.)

export function artworkCoverUrl(gameId) {
  if (gameId === null || gameId === undefined) return null;
  return `/api/library/artwork/cover/${encodeURIComponent(gameId)}`;
}

// ── Admin features endpoint (shared with main app) ───────────────────

/**
 * Load feature flags. This isn't under /api/library — it's the shared
 * /api/admin/features endpoint the main app already uses. Delegates to
 * src/shared/features.js (see that module for the fail-open contract);
 * re-exported here so the library can gate itself without importing from
 * the main bundle.
 */
export const getFeatures = sharedGetFeatures;

// ── Current session ──────────────────────────────────────────────────

/**
 * Resolve the current Better Auth session. Returns user object or null.
 * Uses the same endpoint as the main app's auth.js.
 */
export async function getSession() {
  try {
    const res = await fetch('/api/auth/get-session', { credentials: 'include' });
    if (!res.ok) {
      if (cacheSessionUser !== undefined && cacheSessionUser !== null) {
        clearLibraryResourceCaches();
      }
      cacheSessionUser = null;
      return null;
    }
    const data = await res.json();
    const user = data?.user || null;
    const userId = user?.id || null;
    if (cacheSessionUser !== undefined && cacheSessionUser !== userId) clearLibraryResourceCaches();
    cacheSessionUser = userId;
    return user;
  } catch {
    return null;
  }
}

// ── Trending ────────────────────────────────────────────────────────

/**
 * Fetch trending games. Hits /api/trending/games on the main server
 * (NOT under /api/library), so we bypass the `request()` helper.
 * Returns { games: [...] } on success, or { games: [] } on failure.
 */
export function getTrendingGames(query = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  const url = `/api/trending/games${qs ? '?' + qs : ''}`;
  return fetch(url, { credentials: 'include' })
    .then(r => r.ok ? r.json() : { games: [] });
}
